import { Box, Chip, FormHelperText, SxProps, Typography } from '@mui/material';
import xor from 'lodash/xor';
import React, { useCallback } from 'react';

export interface ExpandedMultiSelectOption<T> {
  value: T;
  label: string | number;
}

export interface ExpandedMultiSelectProps<T> {
  className?: string;
  value?: T[] | null;
  onChange: (value: T[]) => void;
  label: string;
  error?: string;
  required?: boolean;
  options: ExpandedMultiSelectOption<T>[];
}

const ExpandedMultiSelect = <T,>({
  className,
  value,
  label,
  error,
  onChange,
  options,
  required,
}: ExpandedMultiSelectProps<T>) => {
  const onOptionClick = useCallback(
    (option: ExpandedMultiSelectOption<T>) =>
      onChange(xor(value, [option.value])),
    [onChange, value],
  );

  return (
    <Box display="flex" flexDirection="column" gap={1} className={className}>
      <Typography color={!!error ? 'error' : 'textPrimary'} variant="caption">
        {label} {required && '*'}
      </Typography>
      <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1}>
        {options.map((option, index) => (
          <Chip
            size="small"
            onClick={() => onOptionClick(option)}
            color={value?.includes(option.value) ? 'success' : 'default'}
            key={index}
            label={option.label}
          />
        ))}
      </Box>
      {error && (
        <FormHelperText sx={sx} error>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

const sx: SxProps = { marginTop: 0 };

export default ExpandedMultiSelect;
